import { Shell } from "@/components/Shell";
import { Button, buttonVariants } from "@/components/ui/button";
import {
	Empty,
	EmptyContent,
	EmptyDescription,
	EmptyHeader,
	EmptyMedia,
	EmptyTitle,
} from "@/components/ui/empty";
import {
	Item,
	ItemActions,
	ItemContent,
	ItemDescription,
	ItemMedia,
	ItemTitle,
} from "@/components/ui/item";
import { Skeleton } from "@/components/ui/skeleton";
import { cn, getFaviconUrl } from "@/lib/utils";
import { useSuspenseQuery } from "@tanstack/react-query";
import {
	ExternalLinkIcon,
	FolderCodeIcon,
	GlobeIcon,
	SettingsIcon,
} from "lucide-react";
import { Fragment, Suspense } from "react";
import { Link } from "react-router-dom";

interface App {
	id: string;
	name: string;
	website: string | null;
	public: boolean;
}

async function fetchApplications(): Promise<App[]> {
	const res = await fetch("/api/applications", { credentials: "include" });
	if (!res.ok) throw new Error("Failed to load applications");
	const data = (await res.json()) as { applications: App[] };
	return data.applications ?? [];
}

const Header = () => {
	return (
		<div className="flex justify-between items-center">
			<h1 className="text-2xl font-bold tracking-tight text-balance">Applications</h1>
			<Button asChild>
				<Link to="/new">New application</Link>
			</Button>
		</div>
	);
};

const LoadingState = () => {
	return (
		<Fragment>
			<Header />
			{[1, 2, 3].map((i) => (
				<Item variant="outline" key={i}>
					<ItemMedia variant="icon">
						<Skeleton className="h-6 w-6" />
					</ItemMedia>
					<ItemContent>
						<Skeleton className="h-6 w-48" />
						<Skeleton className="h-4 w-32" />
					</ItemContent>
					<ItemActions>
						<Skeleton className="size-9" />
					</ItemActions>
				</Item>
			))}
		</Fragment>
	);
};

const EmptyState = () => {
	return (
		<Empty>
			<EmptyHeader>
				<EmptyMedia variant="icon">
					<FolderCodeIcon />
				</EmptyMedia>
				<EmptyTitle>No applications yet</EmptyTitle>
				<EmptyDescription>
					You haven&apos;t created any applications yet. Get started by creating
					your first application.
				</EmptyDescription>
			</EmptyHeader>
			<EmptyContent className="flex-row justify-center gap-2">
				<Link to="/new" className={buttonVariants({ variant: "default" })}>
					Create application
				</Link>
			</EmptyContent>
			<Link
				to="/new"
				className={cn(
					buttonVariants({ variant: "link", size: "sm" }),
					"text-muted-foreground",
				)}
			>
				Learn More <ExternalLinkIcon />
			</Link>
		</Empty>
	);
};

const Content = () => {
	const { data, error } = useSuspenseQuery({
		queryKey: ["applications"],
		queryFn: fetchApplications,
	});

	if (error)
		return (
			<div className="container max-w-3xl mx-auto p-6 text-destructive">
				{error.message}
			</div>
		);

	if (data.length === 0) return <EmptyState />;

	return (
		<Fragment>
			<Header />
			{data.map((app) => (
				<Item variant="outline" key={app.id}>
					<ItemMedia className="rounded-full">
						{app.website ? (
							<img
								src={getFaviconUrl(app.website)}
								alt="Favicon"
								className="size-8 outline outline-1 -outline-offset-1 outline-white/10"
							/>
						) : (
							<GlobeIcon className="size-6 m-1" />
						)}
					</ItemMedia>
					<ItemContent>
						<ItemTitle>
							{app.name || "Unnamed application"}
							<Link
								to={`/public/${app.id}`}
								target="_blank"
								rel="noopener noreferrer"
							>
								<ExternalLinkIcon className="size-4" />
							</Link>
						</ItemTitle>
						<ItemDescription>
							{app.public ? "Public" : "Private"}
						</ItemDescription>
					</ItemContent>
					<ItemActions>
						<Link
							to={`/${app.id}`}
							className={buttonVariants({
								variant: "outline",
								size: "icon",
							})}
						>
							<SettingsIcon className="size-4" />
						</Link>
					</ItemActions>
				</Item>
			))}
		</Fragment>
	);
};

export const Dashboard = () => {
	return (
		<Shell className="flex flex-col gap-4">
			<Suspense fallback={<LoadingState />}>
				<Content />
			</Suspense>
		</Shell>
	);
};
